import { Response } from "@/models/continente-credentials/common/Response";

export interface IGenericService {
    post<TResponse, TRequest = undefined>(
        url: string,
        payload?: TRequest
    ): Promise<Response<TResponse>>;

    get<TResponse>(
        url: string
    ): Promise<Response<TResponse>>;
}

export class GenericService implements IGenericService {
    async post<TResponse, TRequest = undefined>(
        url: string,
        payload?: TRequest
    ): Promise<Response<TResponse>> {
        const resp = await fetch(url, {
            method: "POST",
            headers: {
                Accept: "application/json, text/plain, */*",
                "Content-Type": "application/json",
            },
            ...(payload !== undefined && {
                body: JSON.stringify(payload),
            }),
        });

        if (resp.ok) {
            return {
                kind: "success",
                value: (await resp.json()) as TResponse,
            };
        }

        if (resp.status === 429) {
            const retryAfter = Number(resp.headers.get("Retry-After"));

            return {
                kind: "rate_limited",
                ...(Number.isFinite(retryAfter) && retryAfter > 0 && {
                    retryAfterSeconds: retryAfter,
                }),
            };
        }

        if (resp.status === 400) {
            let errorBody: any = null;
            try {
                errorBody = await resp.json();
            } catch { }

            const errors = Array.isArray(errorBody)
                ? errorBody
                : errorBody
                    ? [errorBody]
                    : [];

            const returnedError =
                errors.find((e: any) => e?.Code != null) ??
                errors.find((e: any) => e?.code != null) ??
                errors[0];

            return {
                kind: "error",
                code:
                    returnedError?.Code ??
                    returnedError?.code ??
                    returnedError?.statusCode ??
                    0,
                message:
                    returnedError?.message ??
                    returnedError?.Message ??
                    returnedError?.detail ??
                    "",
            };
        }

        return { kind: "unexpected_error" };
    }

    async get<TResponse>(
        url: string
    ): Promise<Response<TResponse>> {
        const resp = await fetch(url, {
            method: "GET",
            headers: {
                Accept: "application/json, text/plain, */*",
                "Content-Type": "application/json",
            }
        });

        if (resp.ok) {
            return {
                kind: "success",
                value: (await resp.json()) as TResponse,
            };
        }

        if (resp.status === 429) {
            const retryAfter = Number(resp.headers.get("Retry-After"));

            return {
                kind: "rate_limited",
                ...(Number.isFinite(retryAfter) && retryAfter > 0 && {
                    retryAfterSeconds: retryAfter,
                }),
            };
        }

        if (resp.status === 400) {
            let errorBody: any = null;
            try {
                errorBody = await resp.json();
            } catch { }

            const errors = Array.isArray(errorBody)
                ? errorBody
                : errorBody
                    ? [errorBody]
                    : [];

            const returnedError =
                errors.find((e: any) => e?.Code != null) ??
                errors.find((e: any) => e?.code != null) ??
                errors[0];

            return {
                kind: "error",
                code:
                    returnedError?.Code ??
                    returnedError?.code ??
                    returnedError?.statusCode ??
                    0,
                message:
                    returnedError?.message ??
                    returnedError?.Message ??
                    returnedError?.detail ??
                    "",
            };
        }

        return { kind: "unexpected_error" };
    }
}

export const genericService = new GenericService();
